import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.addEventListener('slideshow:select', this.#syncVariantFromImage, { signal });
    this.addEventListener('slideshow:select', this.#warmAdjacentImages, { signal });
    this.#warmAdjacentImages();
    this.addEventListener('click', this.#selectThumbnail, { signal, capture: true });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #selectThumbnail = (event) => {
    if (event.button !== 0) return;
    const button = event.target.closest?.('button.slideshow-controls__thumbnail');
    if (!button || button.closest('slideshow-component') !== this.slideshow) return;
    const action = button.getAttribute('on:click') || '';
    const match = action.match(/^\/select\/(\d+)$/);
    if (!match) return;
    event.preventDefault();
    event.stopPropagation();
    this.slideshow.select(Number(match[1]), event, { animate: false });
  };

  #syncVariantFromImage = (event) => {
    if (!event.detail?.userInitiated || event.target !== this.slideshow) return;
    const mediaId = Number(event.detail.slide?.dataset.jeloMediaId);
    if (!mediaId) return;
    const source = this.querySelector('[data-jelo-gallery-variants]');
    const picker = this.closest('.shopify-section')?.querySelector('variant-picker[data-template-product-match="true"]');
    if (!source || !picker) return;
    let data;
    try { data = JSON.parse(source.textContent); } catch { return; }
    const current = data.options.map((name) => picker.getAllSelectedOptions().find((option) => option.name === name)?.value);
    const candidates = data.variants.filter((variant) => variant.media === mediaId && data.options.every((name, index) => !/talla|size|altura/i.test(name) || !current[index] || variant.options[index] === current[index]));
    if (!candidates.length) return;
    candidates.sort((a, b) => b.options.filter((value, index) => value === current[index]).length - a.options.filter((value, index) => value === current[index]).length);
    const variant = candidates[0];
    if (variant.options.every((value, index) => value === current[index])) return;
    let changed;
    data.options.forEach((name, index) => {
      const input = Array.from(picker.querySelectorAll('input[type="radio"]')).find((element) => element.dataset.optionName === name && element.value === variant.options[index]);
      if (input && !input.checked) {
        picker.updateSelectedOption(input);
        changed = input;
      }
      const select = Array.from(picker.querySelectorAll('select')).find((element) => Array.from(element.options).some((option) => option.dataset.optionName === name));
      if (select && select.value !== variant.options[index]) {
        select.value = variant.options[index];
        picker.updateSelectedOption(select);
        changed = select;
      }
    });
    if (changed) {
      const selected = changed instanceof HTMLSelectElement ? changed.selectedOptions[0] : changed;
      selected.dataset.variantId = String(variant.id);
      changed.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  #warmAdjacentImages = () => {
    const slideshow = this.slideshow;
    if (!slideshow) return;
    const slides = Array.from(slideshow.querySelectorAll('slideshow-slide'));
    const current = Number(slideshow.current) || 0;
    for (const index of [current - 1, current + 1]) {
      const image = slides[index]?.querySelector('img.product-media__image');
      if (image instanceof HTMLImageElement && !image.complete) {
        image.loading = 'eager';
        image.fetchPriority = 'low';
      }
    }
  };

  #productSelectVersion = 0;
  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#productSelectVersion++;
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const version = ++this.#productSelectVersion;
    event.promise
      .then(({ detail }) => {
        if (!this.isConnected || version !== this.#productSelectVersion) return;
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const slideshow = this.slideshow;
        const nextSlideshow = newMediaGallery.querySelector('slideshow-component');
        const ids = element => Array.from(element.querySelectorAll('slideshow-slide')).map(slide => slide.getAttribute('slide-id')).join(',');
        if (this.dataset.jeloProductId && this.dataset.jeloProductId === newMediaGallery.dataset.jeloProductId && slideshow && nextSlideshow && ids(slideshow) === ids(nextSlideshow)) {
          const index = Number(nextSlideshow.getAttribute('initial-slide') || '0');
          if (Number.isInteger(index) && index !== slideshow.current) slideshow.select(index, undefined, { animate: false });
          this.#warmAdjacentImages();
          return;
        }
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

